// src/utils/logger.ts
/**
 * 🧠 Pensado por Alcélio Gomes - Codificado com Claude Code
 * 🚀 NeuroAgentes [SimpleX] www.simplex.tec.br
 * 
 * AUDITORIA: Logger centralizado para debug e monitoramento.
 * Implementa logging estruturado com níveis e contexto,
 * mantém consistência de formato em todo o frontend.
 */

export interface Logger {
  error: (message: string, error?: any) => void;
  warn: (message: string, data?: any) => void;
  info: (message: string, data?: any) => void;
  debug: (message: string, data?: any) => void;
}

/**
 * Cria um logger com contexto específico
 * @param context - Contexto do logger (componente, service, etc.)
 * @returns Logger configurado
 */
export function createLogger(context: string): Logger {
  const isDevelopment = process.env.NODE_ENV === 'development';
  
  return {
    error: (message: string, error?: any) => {
      console.error(`[${context}] ❌ ${message}`, error);
    },
    
    warn: (message: string, data?: any) => {
      console.warn(`[${context}] ⚠️ ${message}`, data);
    },
    
    info: (message: string, data?: any) => {
      if (isDevelopment) {
        console.log(`[${context}] ℹ️ ${message}`, data);
      }
    },
    
    debug: (message: string, data?: any) => {
      if (isDevelopment) {
        console.debug(`[${context}] 🐛 ${message}`, data);
      }
    }
  };
}

/**
 * Executa operação com tratamento de erro padronizado
 * @param operation - Função a ser executada
 * @param logger - Logger para registrar erros
 * @param context - Contexto da operação para logs
 * @returns Promise com resultado da operação
 */
export async function executeWithErrorHandling<T>(
  operation: () => Promise<T> | T,
  logger: Logger,
  context: string
): Promise<T> {
  try {
    const result = await operation();
    logger.info(`✅ Sucesso ao ${context}`);
    return result;
  } catch (error: any) {
    const errorMessage = error?.message || 'Erro desconhecido';
    logger.error(`❌ Erro ao ${context}: ${errorMessage}`, error);
    
    // Re-throw with consistent format
    throw new Error(`Falha ao ${context}: ${errorMessage}`);
  }
}

export default {
  createLogger,
  executeWithErrorHandling
};